import React, { useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { X, ChevronRight, Sparkles, Zap } from 'lucide-react';
import {
  ArcaneBlitzGameState,
  SUPERPOWERS,
  ORB_DEFS,
  createInitialArcaneState,
  activateSuperpower,
  getMana,
} from '@/lib/arcane-blitz-engine';
import { NativeChessBoard } from './NativeChessBoard';
import { ArcanePowerPanel } from './ArcanePowerPanel';
import { ArcanePowerOrb } from './ArcanePowerOrb';
import { Position } from '@/lib/chess-engine';

interface ArcaneBlitzTutorialProps {
  onComplete: () => void;
  onSkip: () => void;
}

interface TutorialStep {
  id: number;
  title: string;
  emoji: string;
  description: string;
  instruction: string;
  completionMode: 'button' | 'move' | 'power' | 'orb';
  powerToActivate?: string;
  highlightPanel?: boolean;
}

const STEPS: TutorialStep[] = [
  {
    id: 1,
    title: 'Welcome to Arcane Blitz!',
    emoji: '⚡',
    description: 'Arcane Blitz is chess with superpowers. You earn Mana every turn and spend it to cast devastating spells!',
    instruction: 'Click "Next" to begin learning.',
    completionMode: 'button',
  },
  {
    id: 2,
    title: 'The Mana System',
    emoji: '✨',
    description: 'Every move earns +1 Mana. Capturing an enemy piece earns +2 Mana. You start with 3 Mana.',
    instruction: 'Make any move on the board to earn Mana!',
    completionMode: 'move',
  },
  {
    id: 3,
    title: 'Nova Strike 💥',
    emoji: '💥',
    description: 'Nova Strike (2 Mana) lets you destroy any enemy piece within 2 squares — ignoring normal move rules!',
    instruction: 'Click "Nova Strike" in the panel, then click an enemy piece.',
    completionMode: 'power',
    powerToActivate: 'nova-strike',
    highlightPanel: true,
  },
  {
    id: 4,
    title: 'Arcane Shield 🛡️',
    emoji: '🛡️',
    description: 'Arcane Shield (2 Mana) makes one of your pieces invincible for 1 full turn — it cannot be captured!',
    instruction: 'Click "Arcane Shield" then click one of your own pieces.',
    completionMode: 'power',
    powerToActivate: 'arcane-shield',
    highlightPanel: true,
  },
  {
    id: 5,
    title: 'Phantom Step 👻',
    emoji: '👻',
    description: 'Phantom Step (3 Mana) teleports one of your pieces to ANY empty square on the board instantly!',
    instruction: 'Click "Phantom Step", then click your piece, then click an empty destination.',
    completionMode: 'power',
    powerToActivate: 'phantom-step',
    highlightPanel: true,
  },
  {
    id: 6,
    title: 'Hex Freeze ❄️',
    emoji: '❄️',
    description: 'Hex Freeze (3 Mana) locks an enemy piece in place for 2 turns — it cannot move at all!',
    instruction: 'Click "Hex Freeze" then click an enemy piece to freeze it.',
    completionMode: 'power',
    powerToActivate: 'hex-freeze',
    highlightPanel: true,
  },
  {
    id: 7,
    title: 'Power-Up Orbs',
    emoji: '💎',
    description: 'Glowing orbs appear on the board every 8 moves. Walk onto one to collect its bonus! Mana Gems give +3 Mana instantly.',
    instruction: 'The glowing orb on the board is a Mana Gem. Move any of your pieces onto it!',
    completionMode: 'orb',
  },
  {
    id: 8,
    title: "You're Ready! 🎉",
    emoji: '🎉',
    description: 'You now know the basics of Arcane Blitz! Use your mana wisely, collect orbs, and cast spells to dominate your opponent!',
    instruction: 'Click "Start Real Game" to begin your first Arcane Blitz game!',
    completionMode: 'button',
  },
];

// Build demo states for each step
function buildStepState(stepId: number): ArcaneBlitzGameState {
  const base = createInitialArcaneState();

  if (stepId === 2) {
    return { ...base, whiteMana: 3, blackMana: 3 };
  }

  if (stepId === 3) {
    // 5 mana, enemy pawn near white pieces
    return { ...base, whiteMana: 5, blackMana: 3 };
  }

  if (stepId === 4) {
    return { ...base, whiteMana: 4, blackMana: 3 };
  }

  if (stepId === 5) {
    return { ...base, whiteMana: 5, blackMana: 3 };
  }

  if (stepId === 6) {
    return { ...base, whiteMana: 5, blackMana: 3 };
  }

  if (stepId === 7) {
    // Place a mana gem orb at d5 (row=3, col=3)
    return {
      ...base,
      whiteMana: 3,
      blackMana: 3,
      powerOrbs: [{ position: [3, 3] as Position, type: 'mana-gem' }],
      movesSinceOrbSpawn: 0,
    };
  }

  return base;
}

export function ArcaneBlitzTutorial({ onComplete, onSkip }: ArcaneBlitzTutorialProps) {
  const [stepIndex, setStepIndex] = useState(0);
  const [demoState, setDemoState] = useState<ArcaneBlitzGameState>(() => buildStepState(1));
  const [selectedSquare, setSelectedSquare] = useState<Position | null>(null);
  const [stepCompleted, setStepCompleted] = useState(false);
  const [completionNote, setCompletionNote] = useState('');

  const step = STEPS[stepIndex];
  const isLastStep = stepIndex === STEPS.length - 1;
  const progress = ((stepIndex) / (STEPS.length - 1)) * 100;
  const mana = getMana(demoState, 'white');

  const goToNextStep = useCallback(() => {
    if (isLastStep) {
      onComplete();
      return;
    }
    const nextIndex = stepIndex + 1;
    setStepIndex(nextIndex);
    setDemoState(buildStepState(STEPS[nextIndex].id));
    setSelectedSquare(null);
    setStepCompleted(false);
    setCompletionNote('');
  }, [isLastStep, stepIndex, onComplete]);

  const handleSquareClick = useCallback((pos: Position) => {
    if (step.completionMode === 'button') return;

    const charging = demoState.chargingPower;

    // Power targeting
    if (charging) {
      const def = SUPERPOWERS.find(p => p.type === charging.type)!;

      if (def.needsDoubleTarget) {
        if (!charging.firstTarget) {
          setDemoState(prev => ({
            ...prev,
            chargingPower: { ...charging, firstTarget: pos },
          }));
          return;
        } else {
          const result = activateSuperpower(demoState, charging.type, 'white', charging.firstTarget, pos);
          if (result.success && result.newState) {
            setDemoState({ ...result.newState, chargingPower: null });
            setStepCompleted(true);
            setCompletionNote(`✅ ${def.emoji} ${def.name} activated! Well done!`);
          }
          return;
        }
      } else {
        const result = activateSuperpower(demoState, charging.type, 'white', pos);
        if (result.success && result.newState) {
          setDemoState({ ...result.newState, chargingPower: null });
          setStepCompleted(true);
          setCompletionNote(`✅ ${def.emoji} ${def.name} activated! Well done!`);
        } else {
          setDemoState(prev => ({ ...prev, chargingPower: null }));
        }
        return;
      }
    }

    // Normal move or orb
    const [r, c] = pos;
    const piece = demoState.board[r][c];

    if (selectedSquare) {
      const [sr, sc] = selectedSquare;
      if (sr === r && sc === c) { setSelectedSquare(null); return; }

      // Try move
      try {
        const { validateArcaneMove } = require('@/lib/arcane-blitz-engine');
        const result = validateArcaneMove(demoState, selectedSquare, pos);
        if (result.isLegal && result.newState) {
          setDemoState(result.newState);
          setSelectedSquare(null);

          if (step.completionMode === 'move') {
            setStepCompleted(true);
            setCompletionNote('✅ Move made! You earned Mana!');
          } else if (step.completionMode === 'orb' && result.orbCollected) {
            setStepCompleted(true);
            setCompletionNote('✅ Mana Gem collected! +3 Mana!');
          }
          return;
        }
      } catch {}
      setSelectedSquare(null);
    }

    if (piece && piece.color === 'white') {
      setSelectedSquare(pos);
    }
  }, [demoState, selectedSquare, step]);

  const handleActivatePower = useCallback((type: string) => {
    if (step.completionMode !== 'power') return;
    const def = SUPERPOWERS.find(p => p.type === type)!;
    if (!def) return;

    if (!def.needsTarget) {
      const result = activateSuperpower(demoState, type as any, 'white');
      if (result.success && result.newState) {
        setDemoState({ ...result.newState, chargingPower: null });
        setStepCompleted(true);
        setCompletionNote(`✅ ${def.emoji} ${def.name} activated!`);
      }
      return;
    }

    setDemoState(prev => ({
      ...prev,
      chargingPower: { type: type as any },
    }));
  }, [demoState, step.completionMode]);

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4">
      <div className="w-full max-w-5xl bg-gradient-to-br from-violet-950 via-purple-900/80 to-indigo-950 border border-teen-purple/40 rounded-2xl shadow-2xl overflow-hidden max-h-[95vh] flex flex-col">
        
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-teen-purple/30">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-teen-purple/20 rounded-lg">
              <Sparkles className="w-5 h-5 text-teen-cyan animate-sparkle" />
            </div>
            <div>
              <h2 className="font-bold text-lg bg-gradient-to-r from-teen-cyan via-teen-pink to-teen-purple bg-clip-text text-transparent">
                Arcane Blitz Tutorial
              </h2>
              <p className="text-xs text-muted-foreground">Step {stepIndex + 1} of {STEPS.length}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="border-teen-cyan/40 text-teen-cyan hidden sm:flex">
              {mana}✨ mana
            </Badge>
            <Button variant="ghost" size="icon" onClick={onSkip} className="text-muted-foreground hover:text-foreground">
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Progress bar */}
        <div className="px-4 pt-3">
          <Progress value={progress} className="h-1.5 bg-violet-900/50" />
          <div className="flex justify-between mt-1">
            {STEPS.map((s, i) => (
              <div
                key={s.id}
                className={`w-6 h-6 rounded-full text-xs flex items-center justify-center border transition-all ${
                  i < stepIndex
                    ? 'bg-teen-cyan border-teen-cyan text-black'
                    : i === stepIndex
                    ? 'bg-teen-purple border-teen-purple text-white'
                    : 'bg-transparent border-teen-purple/30 text-muted-foreground'
                }`}
              >
                {i < stepIndex ? '✓' : i + 1}
              </div>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-4">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-4 h-full">
            
            {/* Left: Board */}
            <div className="space-y-3">
              {step.completionMode !== 'button' ? (
                <div className="relative">
                  <NativeChessBoard
                    gameState={demoState}
                    onSquareClick={handleSquareClick}
                    selectedSquare={selectedSquare}
                    highlightedSquares={[
                      ...demoState.frozenPieces.map(f => f.position),
                      ...demoState.shieldedPieces.map(s => s.position),
                    ]}
                    flipped={false}
                  />
                  {/* Orb overlays */}
                  {demoState.powerOrbs.map((orb, i) => {
                    const [r, c] = orb.position;
                    return (
                      <div
                        key={i}
                        className="absolute pointer-events-none z-20 flex items-center justify-center"
                        style={{
                          left: `calc(${c} * 12.5% + 12.5% / 2 - 10px)`,
                          top: `calc(${r} * 12.5% + 12.5% / 2 - 10px)`,
                          width: 20,
                          height: 20,
                        }}
                      >
                        <ArcanePowerOrb type={orb.type} />
                      </div>
                    );
                  })}
                  {/* Frozen overlays */}
                  {demoState.frozenPieces.map((fp, i) => {
                    const [r, c] = fp.position;
                    return (
                      <div
                        key={`freeze-${i}`}
                        className="absolute pointer-events-none z-20 flex items-center justify-center text-sm"
                        style={{
                          left: `calc(${c} * 12.5%)`,
                          top: `calc(${r} * 12.5%)`,
                          width: '12.5%',
                          height: '12.5%',
                        }}
                      >
                        ❄️
                      </div>
                    );
                  })}
                  {/* Targeting indicator */}
                  {demoState.chargingPower && (
                    <div className="absolute inset-0 border-4 border-teen-purple/60 rounded animate-pulse pointer-events-none z-30" />
                  )}
                </div>
              ) : (
                /* Button-only step: show a decorative graphic */
                <div className="flex items-center justify-center min-h-[300px]">
                  <div className="text-center space-y-6">
                    <div className="text-8xl animate-bounce">{step.emoji}</div>
                    {stepIndex === 0 && (
                      <div className="grid grid-cols-3 gap-3 max-w-xs mx-auto">
                        {SUPERPOWERS.slice(0, 6).map(sp => (
                          <div key={sp.type} className="bg-teen-purple/20 rounded-lg p-2 text-center border border-teen-purple/30">
                            <div className="text-2xl">{sp.emoji}</div>
                            <div className="text-xs text-muted-foreground mt-1">{sp.name}</div>
                            <Badge variant="outline" className="text-xs border-teen-cyan/40 text-teen-cyan mt-1">
                              {sp.cost}✨
                            </Badge>
                          </div>
                        ))}
                      </div>
                    )}
                    {isLastStep && (
                      <div className="space-y-2">
                        <div className="text-foreground font-semibold text-lg">Superpowers mastered:</div>
                        <div className="flex flex-wrap justify-center gap-2">
                          {SUPERPOWERS.map(sp => (
                            <Badge key={sp.type} className="bg-teen-purple/30 border-teen-purple/50 text-foreground">
                              {sp.emoji} {sp.name}
                            </Badge>
                          ))}
                        </div>
                        <div className="text-muted-foreground text-sm mt-3">
                          Plus 4 power orbs to collect on the board!
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Right: Panel */}
            <div className="space-y-3">
              {/* Step info card */}
              <Card className="border-teen-purple/30 bg-violet-950/60">
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">{step.emoji}</span>
                    <h3 className="font-bold text-foreground">{step.title}</h3>
                  </div>
                  <p className="text-sm text-foreground/80 leading-relaxed">{step.description}</p>
                  <div className={`p-3 rounded-lg border text-sm ${
                    stepCompleted
                      ? 'bg-teen-green/10 border-teen-green/40 text-teen-green'
                      : 'bg-teen-cyan/10 border-teen-cyan/30 text-teen-cyan'
                  }`}>
                    {stepCompleted ? completionNote : `💡 ${step.instruction}`}
                  </div>
                </CardContent>
              </Card>

              {/* Power panel for relevant steps */}
              {step.completionMode === 'power' && (
                <div className={step.highlightPanel ? 'ring-2 ring-teen-cyan/60 rounded-lg' : ''}>
                  <ArcanePowerPanel
                    state={demoState}
                    playerColor="white"
                    onActivate={handleActivatePower}
                    isPlayerTurn={true}
                  />
                </div>
              )}

              {/* Orb info for step 7 */}
              {step.id === 7 && (
                <Card className="border-teen-cyan/30 bg-violet-950/60">
                  <CardContent className="p-3">
                    <p className="text-xs text-muted-foreground mb-2 uppercase tracking-wider">Power Orbs</p>
                    <div className="space-y-2">
                      {ORB_DEFS.map(orb => (
                        <div key={orb.type} className="flex items-center gap-2 text-xs">
                          <ArcanePowerOrb type={orb.type} size="sm" />
                          <div>
                            <span className="text-foreground font-medium">{orb.name}</span>
                            <span className="text-muted-foreground ml-1">— {orb.description}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Navigation */}
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onSkip}
                  className="text-muted-foreground hover:text-foreground flex-1"
                >
                  Skip Tutorial
                </Button>
                <Button
                  onClick={goToNextStep}
                  disabled={step.completionMode !== 'button' && !stepCompleted}
                  className={`flex-1 font-semibold ${
                    isLastStep
                      ? 'bg-gradient-to-r from-teen-cyan to-teen-purple hover:opacity-90 text-white'
                      : 'bg-teen-purple hover:bg-teen-purple/90 text-white'
                  }`}
                >
                  {isLastStep ? (
                    <>
                      <Zap className="w-4 h-4 mr-2" />
                      Start Real Game
                    </>
                  ) : step.completionMode === 'button' ? (
                    <>
                      Next
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </>
                  ) : stepCompleted ? (
                    <>
                      Continue
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </>
                  ) : (
                    'Complete the action above ↑'
                  )}
                </Button>
              </div>

              {/* Step dots */}
              <div className="flex justify-center gap-1.5">
                {STEPS.map((_, i) => (
                  <div
                    key={i}
                    className={`h-1.5 rounded-full transition-all ${
                      i === stepIndex ? 'w-6 bg-teen-cyan' : i < stepIndex ? 'w-3 bg-teen-purple/60' : 'w-3 bg-teen-purple/20'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
